/**
 * @file MostVisitedPages.js
 * @description Card for the Most Visited Pages dashboard.
 */

import React, { useState, useEffect } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    LabelList,
} from 'recharts';
import { getMaxItemsByField } from '../../utils/utils';
import {
    DASHBOARD_MOST_VISITED_PAGES_MAX_ITEMS,
    DASHBOARD_MOST_VISITED_PAGES_Y_AXIS_LABEL_CHARACTERS_MAX,
    DASHBOARD_MOST_VISITED_PAGES_BAR_HEIGHT,
} from './DashboardUtils';
import NotEnoughData from './NotEnoughData';
import CustomDashboardTooltip from '../helpers/CustomDashboardTooltip';
import './MostVisitedPages.scss';
import { getText } from '../../i18n';

const mapApiResponseToMostVisitedPagesData = dataFromApi => {
    const topItems = getMaxItemsByField(
        dataFromApi,
        DASHBOARD_MOST_VISITED_PAGES_MAX_ITEMS,
        'count',
    );
    return topItems.map(({ name, count }) => ({
        name: name || 'Unknown',
        count: count,
    }));
};

const MostVisitedPages = ({ mostVisitedPagesData }) => {
    if (!mostVisitedPagesData || mostVisitedPagesData?.length === 0) {
        return <NotEnoughData />;
    }

    const [chartData, setChartData] = useState(mostVisitedPagesData);
    const [totalVisitedPages, setTotalVisitedPages] = useState(0);
    useEffect(() => {
        if (mostVisitedPagesData?.length > 0) {
            const mappedData =
                mapApiResponseToMostVisitedPagesData(mostVisitedPagesData);
            setChartData(mappedData);
            const totalViews = mappedData.reduce(
                (acc, { count }) => acc + count,
                0,
            );
            setTotalVisitedPages(totalViews);
        }
    }, [mostVisitedPagesData]);

    return (
        <div className="most-visited-pages-container">
            <div className="most-visited-pages-views-container">
                <div className="most-visited-pages-views-count">
                    {totalVisitedPages.toLocaleString()}
                </div>
                <div className="most-visited-pages-views-text">{getText('msg_d7f83b735dd4')}</div>
            </div>
            <ResponsiveContainer
                width="100%"
                height={Math.max(
                    chartData.length * DASHBOARD_MOST_VISITED_PAGES_BAR_HEIGHT,
                )}
            >
                {' '}
                {/* Responsively sets the height in px for the chart to keep spacing in between each entry the same regardless of data amount */}
                <BarChart
                    layout="vertical"
                    data={chartData}
                    margin={{ top: 0, right: 40, left: 5, bottom: 0 }}
                    barSize={12}
                >
                    <defs>
                        <linearGradient
                            id="gradientColor"
                            x1="1"
                            y1="0"
                            x2="0"
                            y2="0"
                        >
                            <stop offset="5.56%" stopColor="#1890FF" />
                            <stop
                                offset="100%"
                                stopColor="rgba(67, 24, 255, 0.28)"
                            />
                        </linearGradient>
                    </defs>
                    <XAxis type="number" hide />
                    <YAxis
                        dataKey="name"
                        type="category"
                        width={100}
                        interval={0}
                        tick={({ x, y, payload }) => {
                            const text = payload.value || '';
                            const truncated =
                                text.length >
                                DASHBOARD_MOST_VISITED_PAGES_Y_AXIS_LABEL_CHARACTERS_MAX
                                    ? `${text.slice(0, DASHBOARD_MOST_VISITED_PAGES_Y_AXIS_LABEL_CHARACTERS_MAX)}…`
                                    : text;
                            return (
                                <text
                                    x={x}
                                    y={y}
                                    fill="#7E7C8A"
                                    className="most-visited-pages-yAxis-tick-label"
                                >
                                    {truncated}
                                </text>
                            );
                        }}
                        axisLine={false}
                        tickLine={false}
                    />
                    <Tooltip
                        content={({ active, payload }) => {
                            if (payload && payload.length > 0) {
                                const { name, count } = payload[0].payload;
                                return (
                                    <CustomDashboardTooltip
                                        active={active}
                                        label={name}
                                        value={count}
                                        unit="views"
                                    />
                                );
                            }
                        }}
                        cursor={false}
                    />
                    <Bar
                        dataKey="count"
                        radius={[2, 2, 2, 2]}
                        fill="url(#gradientColor)"
                    >
                        <LabelList
                            dataKey="count"
                            position="right"
                            className="bar-right-label"
                            formatter={value => value.toLocaleString('en-US')}
                        />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};
export default MostVisitedPages;
